import { Injectable } from '@angular/core';
import { AdminMessages } from '@app/admin/admin.messages';
import { NotifyUtils } from '@app/shared/utils/notify.utils';
import { Observable, firstValueFrom } from 'rxjs';
import { CompetencyAPIService } from '../api/competency-api.service';
import { Competency } from '../model/competency.model';
import { CreateCompetencyDto } from '../model/create-competency.model';
import { TranslateService } from '@ngx-translate/core';
import { Globals } from '@app/shared/globals/globals';
import { InFlightRegistry } from '@app/shared/utils/in-flight-registry';

// Single-slot guard key for the file import, which allows only one concurrent run.
const IMPORT_FILE_KEY = 'import-file';

@Injectable({
  providedIn: 'root'
})
export class CompetencyBusinessService {

  // key -> the write currently in flight for that key. A duplicate call while one is in flight is
  // coalesced onto the same promise (see InFlightRegistry) rather than rejected.
  updating: InFlightRegistry;
  downloadingCSV: boolean;

  constructor(
    private competencyAPI: CompetencyAPIService,
    private translateService: TranslateService,
    private notifyUtils: NotifyUtils,
    private globals: Globals
  ) {
    this.updating = new InFlightRegistry();
    this.downloadingCSV = false;
  }

  create(createDto: CreateCompetencyDto): Promise<Competency> {
    return firstValueFrom(this.competencyAPI.create(createDto))
      .then(res => {
        const message = this.translateService.instant(AdminMessages.COMPETENCIES.SUCCESS_COMPETENCY_CREATE, this.globals.terminology);
        this.notifyUtils.notify(message);
        return res;
      });
  }

  update(id: number, updateDto: CreateCompetencyDto): Promise<Competency> {
    return this.updating.run(id, () =>
      firstValueFrom(this.competencyAPI.update(id, updateDto))
        .then(res => {
          const message = this.translateService.instant(AdminMessages.COMPETENCIES.SUCCESS_COMPETENCY_UPDATE, this.globals.terminology);
          this.notifyUtils.notify(message);
          return res;
        })
    );
  }

  delete(id: number): Promise<Competency> {
    return this.updating.run(id, () =>
      firstValueFrom(this.competencyAPI.delete(id))
        .then(res => {
          const message = this.translateService.instant(AdminMessages.COMPETENCIES.SUCCESS_DELETE_COMPETENCY_SINGLE, this.globals.terminology);
          this.notifyUtils.notify(message);
          return res;
        })
    );
  }

  deleteMultiple(ids: number[]): Promise<Competency[]> {
    return this.updating.run(ids.join(','), () =>
      firstValueFrom(this.competencyAPI.deleteMultiple(ids))
        .then(res => {
          const message = this.translateService.instant(AdminMessages.COMPETENCIES.SUCCESS_DELETE_COMPETENCY_MULTIPLE, this.globals.terminology);
          this.notifyUtils.notify(message);
          return res;
        })
    );
  }

  importFile(file: File): Promise<Competency[]> {
    return this.updating.run(IMPORT_FILE_KEY, () =>
      firstValueFrom(this.competencyAPI.importCompetencies(file))
        .then(res => {
          const message = this.translateService.instant(AdminMessages.COMPETENCIES.SUCCESS_IMPORT_COMPETENCIES, this.globals.terminology);
          this.notifyUtils.notify(message);
          return res;
        })
    );
  }

  getCompetencies(
    ids: number[],
    name: string,
    categories: string[]
  ): Observable<Competency[]> {
    return this.competencyAPI.get(ids, name, categories);
  }


  downloadCSV(): Promise<Blob> {
    if (this.downloadingCSV) { return; }
    this.downloadingCSV = true;

    firstValueFrom(this.competencyAPI.downloadCSV())
      .then(res => {
        const url = window.URL.createObjectURL(res);
        const a = document.createElement('a');
        a.href = url;
        a.download = `frankli-competencies-${new Date().toISOString().split('T')[0]}.csv`;
        a.click();
      })
      .finally(() => {
        this.downloadingCSV = false;
      });
  }

  getForUserId(userId: number): Promise<Competency[]> {
    return firstValueFrom(this.competencyAPI.getForUserId(userId));
  }

}
